import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { FileText, Search, Plus, Eye, Edit, RefreshCw, X, Calendar } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';

const POCPlusPage = () => {
  const navigate = useNavigate();
  const [pocs, setPocs] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    patientId: '', patientName: '', certPeriodStart: '', certPeriodEnd: '',
    physicianName: '', diagnoses: '', goals: '', interventions: '', frequency: '', status: 'Draft'
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pocsRes, patientsRes] = await Promise.all([
        api.get('/poc485'),
        api.get('/patients')
      ]);
      if (pocsRes.data.success) setPocs(pocsRes.data.pocs || []);
      if (patientsRes.data.success) setPatients(patientsRes.data.patients || []);
    } catch (error) {
      toast.error('Failed to load plans of care');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.patientName) { toast.error('Please select a patient'); return; }
    try {
      const res = await api.post('/poc485', formData);
      if (res.data.success) {
        toast.success('Plan of Care Plus created');
        setPocs(prev => [...prev, res.data.poc]);
        setShowModal(false);
        setFormData({
          patientId: '', patientName: '', certPeriodStart: '', certPeriodEnd: '',
          physicianName: '', diagnoses: '', goals: '', interventions: '', frequency: '', status: 'Draft'
        });
      }
    } catch (error) {
      toast.error('Failed to create plan of care');
    }
  };

  const filteredPocs = pocs.filter(p =>
    p.patientName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = {
    total: pocs.length,
    active: pocs.filter(p => p.status === 'Active' || p.status === 'Signed').length,
    pending: pocs.filter(p => p.status === 'Draft' || p.status === 'Pending').length,
    expiringSoon: pocs.filter(p => {
      if (!p.certPeriodEnd) return false;
      const endDate = new Date(p.certPeriodEnd);
      const now = new Date();
      const daysUntilExpiry = (endDate - now) / (1000 * 60 * 60 * 24);
      return daysUntilExpiry > 0 && daysUntilExpiry <= 14;
    }).length
  };

  return (
    <DashboardLayout>
      <div className="p-6" data-testid="poc-plus-page">
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-6 h-6 text-cyan-600" />
                Plan of Care Plus
              </h1>
              <p className="text-gray-500 text-sm mt-1">Extended plan of care management with goals, interventions, and tracking</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchData} variant="outline"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
              <Button className="bg-cyan-600 hover:bg-cyan-700 text-white" onClick={() => setShowModal(true)}>
                <Plus className="w-4 h-4 mr-2" />New POC Plus
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-cyan-700">{stats.total}</div>
            <div className="text-sm text-cyan-600">Total Plans</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-700">{stats.active}</div>
            <div className="text-sm text-green-600">Active</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
            <div className="text-sm text-yellow-600">Pending</div>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-orange-700">{stats.expiringSoon}</div>
            <div className="text-sm text-orange-600">Expiring Soon</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search by patient..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-32"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : filteredPocs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No plans of care found. Click "New POC Plus" to create one.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Patient</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Cert Period</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Physician</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Frequency</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredPocs.map((poc) => (
                  <tr key={poc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{poc.patientName}</td>
                    <td className="px-6 py-4">
                      {poc.certPeriodStart && poc.certPeriodEnd ? 
                        `${poc.certPeriodStart} - ${poc.certPeriodEnd}` : '-'}
                    </td>
                    <td className="px-6 py-4">{poc.physicianName || '-'}</td>
                    <td className="px-6 py-4">{poc.frequency || '-'}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        poc.status === 'Active' || poc.status === 'Signed' ? 'bg-green-100 text-green-800' :
                        poc.status === 'Draft' ? 'bg-gray-100 text-gray-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {poc.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline"><Eye className="w-4 h-4" /></Button>
                        <Button size="sm" variant="outline"><Edit className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 m-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">New Plan of Care Plus</h2>
                <button onClick={() => setShowModal(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                <div>
                  <label className="block text-sm font-medium mb-1">Patient *</label>
                  <select value={formData.patientId} onChange={(e) => {
                    const p = patients.find(p => p.id === e.target.value);
                    setFormData(prev => ({ ...prev, patientId: e.target.value, patientName: p ? `${p.firstName} ${p.lastName}` : '' }));
                  }} className="w-full border rounded-md px-3 py-2">
                    <option value="">Select Patient</option>
                    {patients.map(p => <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Certification Start</label>
                    <Input type="date" value={formData.certPeriodStart} onChange={(e) => setFormData(prev => ({ ...prev, certPeriodStart: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Certification End</label>
                    <Input type="date" value={formData.certPeriodEnd} onChange={(e) => setFormData(prev => ({ ...prev, certPeriodEnd: e.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Physician Name</label>
                    <Input value={formData.physicianName} onChange={(e) => setFormData(prev => ({ ...prev, physicianName: e.target.value }))} placeholder="Dr. Smith" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Visit Frequency</label>
                    <Input value={formData.frequency} onChange={(e) => setFormData(prev => ({ ...prev, frequency: e.target.value }))} placeholder="e.g., 3x/week for 4 weeks" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Primary Diagnoses</label>
                  <textarea value={formData.diagnoses} onChange={(e) => setFormData(prev => ({ ...prev, diagnoses: e.target.value }))} className="w-full border rounded-md px-3 py-2" rows={2} placeholder="Enter diagnoses..." />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Goals</label>
                  <textarea value={formData.goals} onChange={(e) => setFormData(prev => ({ ...prev, goals: e.target.value }))} className="w-full border rounded-md px-3 py-2" rows={3} placeholder="Enter patient goals..." />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Interventions</label>
                  <textarea value={formData.interventions} onChange={(e) => setFormData(prev => ({ ...prev, interventions: e.target.value }))} className="w-full border rounded-md px-3 py-2" rows={3} placeholder="Enter planned interventions..." />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button className="bg-cyan-600 hover:bg-cyan-700 text-white" onClick={handleSubmit}>Create POC Plus</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default POCPlusPage;
